import React, { useEffect, useState } from "react";
import axios from "axios";
import { Pencil } from "lucide-react";

export default function Token() {
  const [showTokenForm, setShowTokenForm] = useState(false);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [tokenName, setTokenName] = useState("");
  const [tokenNames, setTokenNames] = useState([]);
  const [tokenAmount, setTokenAmount] = useState("");
  const [tokenData, setTokenData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [minLiveToken, setMinLiveToken] = useState('');
  const [maxLiveToken, setMaxLiveToken] = useState('');
  const [minVideoToken, setMinVideoToken] = useState('');
  const [maxVideoToken, setMaxVideoToken] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const StudentsPerPage = 16;
  const [showTokenDetails, setShowTokenDetails] = useState(false);
  const [studentTokens, setStudentTokens] = useState([]);
  const [selectedStudentName, setSelectedStudentName] = useState('');
  const [showStudentDropdown, setShowStudentDropdown] = useState(false); // Added state for dropdown
  const [studentSearchQuery, setStudentSearchQuery] = useState(''); // Separate search query for student selection

  // Filter students for the searchable dropdown
  const filteredStudents = students.filter((student) =>
    `${student.first_name} ${student.surname} ${student.student_id} ${student.phone || ''}`
      .toLowerCase()
      .includes(studentSearchQuery.toLowerCase())
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studentResponse, tokenResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudent`),
          axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudentToken`),
          axios.get(`${process.env.REACT_APP_API_URL}/admin/getTokenName`), // Include this if needed in parallel
        ]);
  
        console.log("Students:", studentResponse.data);
        setStudents(studentResponse.data.data || []);
  
        console.log("Token Data:", tokenResponse.data);
        const rawTokenData = tokenResponse.data.data || [];
  
        // Merge tokenData with student_login_id from students
        const mergedTokenData = rawTokenData.map(token => {
          const matchingStudent = studentResponse.data.data.find(
            student => student.student_id === token.student_id
          );
          return {
            ...token,
            student_login_id: matchingStudent ? matchingStudent.student_login_id : null, // Fallback to null if no match
          };
        });
  
        setTokenData(mergedTokenData);
  
        const tokenNameResponse = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getTokenName`);
        console.log("Token Names:", tokenNameResponse.data);
        setTokenNames(tokenNameResponse.data.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const handleEditToken = async (studentId, firstName) => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudentTokenDetails/${studentId}`);
      setStudentTokens(response.data.data || []);
      setSelectedStudentName(firstName);
      setShowTokenDetails(true);
    } catch (error) {
      console.error("Error fetching student token details:", error);
      alert("Failed to fetch token details.");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = () => setShowTokenForm(true);

  const handleTokenSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStudent || !tokenName || !tokenAmount) {
      alert("Please fill out all fields.");
      return;
    }

    setLoading(true);
    const formData = {
      student_id: selectedStudent,
      token_name: tokenName,
      amount: tokenAmount,
    };

    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/admin/addToken`, formData);
      if (response.status === 200 || response.status === 201) {
        alert("Token successfully added!");
        setShowTokenForm(false);
        setSelectedStudent("");
        setTokenName("");
        setTokenAmount("");
        setStudentSearchQuery(""); // Reset student search query
        const updatedTokenData = await axios.get(`${process.env.REACT_APP_API_URL}/admin/getStudentToken`);
        setTokenData(updatedTokenData.data.data || []);
      }
    } catch (error) {
      console.error("Error adding token:", error);
      alert("Failed to add token.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setShowTokenForm(false);
    setSelectedStudent("");
    setTokenName("");
    setTokenAmount("");
    setStudentSearchQuery(""); // Reset student search query
  };

  const handleCloseDetailsModal = () => {
    setShowTokenDetails(false);
    setStudentTokens([]);
    setSelectedStudentName('');
  };

  const filteredTokenData = tokenData.filter((s) => {
    const nameMatch = `${s.student_id} ${s.first_name} ${s.surname} ${s.chinese_name}`.toLowerCase().includes(searchQuery.toLowerCase());

    const liveTokenMatch =
      (minLiveToken === '' || s.live_token_amount >= parseInt(minLiveToken)) &&
      (maxLiveToken === '' || s.live_token_amount <= parseInt(maxLiveToken));

    const videoTokenMatch =
      (minVideoToken === '' || s.video_token_amount >= parseInt(minVideoToken)) &&
      (maxVideoToken === '' || s.video_token_amount <= parseInt(maxVideoToken));

    return nameMatch && liveTokenMatch && videoTokenMatch;
  });

  const indexOfLast = currentPage * StudentsPerPage;
  const indexOfFirst = indexOfLast - StudentsPerPage;
  const currentStudents = filteredTokenData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredTokenData.length / StudentsPerPage);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
    setCurrentPage(1);
  };

  return (
    <div className="p-8">
      <h1 className="text-2xl font-bold">Student Token Management</h1>
      <div className="bg-neutral-200 p-4">
        <div className="mb-4 flex flex-col md:flex-row md:items-center md:gap-4">
          <input
            type="text"
            placeholder="Search by name, ID"
            value={searchQuery}
            onChange={handleSearch}
            className="w-[300px] px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        <div className="mt-2 mb-4 border-t border-gray-400 w-9/12 mx-auto"></div>
        <button
          onClick={handleCreateToken}
          className="px-4 py-2 bg-blue-400 text-white rounded hover:bg-blue-500 absolute right-10"
        >
          Edit Token
        </button>
        <table className="w-full border-collapse bg-white">
          <thead>
            <tr>
              <th className="border px-4 py-2">Student ID</th>
              <th className="border px-4 py-2">Student Login ID</th>
              <th className="border px-4 py-2">Student Name</th>
              <th className="border px-4 py-2">Live Token Amount</th>
              <th className="border px-4 py-2">Video Token Amount</th>
              <th className="border px-4 py-2">Action</th>
            </tr>
          </thead>
          <tbody>
            {currentStudents.map((row) => (
              <tr key={row.student_id}>
                <td className="border px-4 py-2">{row.student_id}</td>
                <th className="border px-4 py-2">{row.student_login_id}</th>
                <td className="border px-4 py-2">{row.first_name}</td>
                <td className="border px-4 py-2">{row.live_token_amount}</td>
                <td className="border px-4 py-2">{row.video_token_amount}</td>
                <td className="border px-4 py-2 text-center">
                  <button
                    onClick={() => handleEditToken(row.student_id, row.first_name)}
                    className="text-blue-500 hover:text-blue-700"
                  >
                    <Pencil size={20} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showTokenForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-1/2 max-w-lg rounded-lg shadow-lg p-6 relative">
            <h2 className="text-xl font-semibold mb-4">Add Token for Student</h2>
            <form onSubmit={handleTokenSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 mb-1">Select Student:</label>
                <div className="relative">
                    <input
                        type="text"
                        value={studentSearchQuery}
                        onChange={(e) => {
                            setStudentSearchQuery(e.target.value);
                            setShowStudentDropdown(true);
                        }}
                        onFocus={() => setShowStudentDropdown(true)}
                        placeholder="Search by name, ID, or phone..."
                        className="border rounded w-full py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {showStudentDropdown && (
                        <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
                            {filteredStudents.length > 0 ? (
                                filteredStudents.map((student) => (
                                    <li
                                        key={student.student_id}
                                        onClick={() => {
                                            setSelectedStudent(student.student_id);
                                            setStudentSearchQuery(`${student.surname} ${student.first_name} (${student.student_id})`);
                                            setShowStudentDropdown(false);
                                        }}
                                        className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                                    >
                                        {`${student.first_name} ${student.surname} (${student.student_id}) - ${student.phone || 'No phone'}`}
                                    </li>
                                ))
                            ) : (
                                <li className="px-4 py-2 text-gray-500">No students found</li>
                            )}
                        </ul>
                    )}
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-1">Token Name:</label>
                <select
                  value={tokenName}
                  onChange={(e) => setTokenName(e.target.value)}
                  className="border rounded w-full py-2 px-3"
                  required
                >
                  <option value="" disabled>Select a token</option>
                  {tokenNames.map((token) => (
                    <option key={token.token_name} value={token.token_name}>
                      {token.token_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 mb-1">Token Amount:</label>
                <input
                  type="number"
                  value={tokenAmount}
                  onChange={(e) => setTokenAmount(e.target.value)}
                  className="border rounded w-full py-2 px-3"
                  required
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="bg-gray-500 text-white px-4 py-2 mr-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 ${loading && "opacity-50 cursor-not-allowed"}`}
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTokenDetails && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 relative w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">{`Token Details for ${selectedStudentName}`}</h2>
            {studentTokens.length > 0 ? (
              <div className="mb-6">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="border px-4 py-2 text-left">Token Name</th>
                      <th className="border px-4 py-2 text-left">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentTokens.map((token) => (
                      <tr key={token.token_name}>
                        <td className="border px-4 py-2">{token.token_name}</td>
                        <td className="border px-4 py-2">{token.amount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mb-6 text-gray-600">No tokens found for this student.</p>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleCloseDetailsModal}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mt-6">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <select
          value={currentPage}
          onChange={(e) => setCurrentPage(Number(e.target.value))}
          className="px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
            <option key={page} value={page}>
              Page {page} of {totalPages}
            </option>
          ))}
        </select>
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          className="px-3 py-1 bg-gray-300 text-gray-800 rounded disabled:opacity-50"
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}